/**
 * Production Configuration
 * Optimized settings for production deployment
 */

export const PRODUCTION_CONFIG = {
  // Performance Settings
  performance: {
    enableGzip: true,
    enableBrotli: true,
    enableMinification: true,
    enableTreeShaking: true,
    enableCodeSplitting: true,
    enableLazyLoading: true,
    enablePreloading: true,
    enableServiceWorker: true,
    enableCaching: true,
    cacheStrategy: 'stale-while-revalidate'
  },

  // Build Settings
  build: {
    target: 'esnext',
    minify: 'terser',
    sourcemap: false,
    reportCompressedSize: true,
    chunkSizeWarningLimit: 500,
    assetsInlineLimit: 2048,
    cssCodeSplit: true
  },

  // CDN Configuration
  cdn: {
    enabled: true,
    baseUrl: 'https://cdn.yourdomain.com',
    assetsPath: '/assets',
    imagesPath: '/images',
    fontsPath: '/fonts'
  },

  // Caching Configuration
  caching: {
    staticAssets: {
      maxAge: 31536000, // 1 year
      immutable: true
    },
    apiResponses: {
      maxAge: 300, // 5 minutes
      staleWhileRevalidate: 3600 // 1 hour
    },
    htmlPages: {
      maxAge: 0,
      mustRevalidate: true
    }
  },

  // Monitoring Configuration
  monitoring: {
    enableAnalytics: true,
    enableErrorTracking: true,
    enablePerformanceMonitoring: true,
    enableUserTracking: false, // GDPR compliance
    analyticsEndpoint: import.meta.env.VITE_ANALYTICS_ENDPOINT,
    errorEndpoint: import.meta.env.VITE_ERROR_ENDPOINT,
    metricsEndpoint: import.meta.env.VITE_METRICS_ENDPOINT
  },

  // Security Configuration
  security: {
    enableCSP: true,
    enableHSTS: true,
    enableXSSProtection: true,
    enableCSRFProtection: true,
    enableRateLimiting: true,
    enableInputValidation: true,
    enableOutputEncoding: true
  },

  // API Configuration
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
    timeout: 10000,
    retryAttempts: 3,
    retryDelay: 1000,
    enableCaching: true,
    cacheTimeout: 300000 // 5 minutes
  },

  // Mapping APIs Configuration
  apis: {
    opencage: {
      apiKey: import.meta.env.VITE_OPENCAGE_API_KEY || 'bac3764f100747d4888860391e1a99a5',
      baseUrl: 'https://api.opencagedata.com/geocode/v1/json',
      timeout: 5000,
      rateLimit: 2500 // requests per day
    },
    openStreetMap: {
      baseUrl: 'https://nominatim.openstreetmap.org/search',
      timeout: 5000,
      rateLimit: 1000 // requests per hour
    },
    googleMaps: {
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      baseUrl: 'https://maps.googleapis.com/maps/api',
      timeout: 5000,
      rateLimit: 1000 // requests per day
    }
  },

  // Cache Configuration
  cache: {
    suggestionsTTL: 300000, // 5 minutes
    geocodingTTL: 3600000, // 1 hour
    routingTTL: 1800000, // 30 minutes
    cleanupInterval: 600000 // 10 minutes
  },

  // Performance Configuration
  performance: {
    minQueryLength: 2,
    maxSuggestions: 8,
    earlyReturnThreshold: 5,
    earlyReturnScore: 0.8,
    enableParallelProcessing: true,
    enableCaching: true,
    enableLearning: true
  },

  // Learning Configuration
  learning: {
    maxLearnedLocations: 1000,
    minSearchCount: 2,
    maxAge: 30 * 24 * 60 * 60 * 1000 // 30 days
  },

  // Error Handling Configuration
  errorHandling: {
    maxRetries: 3,
    retryDelay: 1000,
    enableFallback: true,
    enableLogging: true
  },

  // Database Configuration
  database: {
    enableConnectionPooling: true,
    maxConnections: 10,
    connectionTimeout: 30000,
    queryTimeout: 10000,
    enableQueryCaching: true,
    cacheTimeout: 300000 // 5 minutes
  },

  // Logging Configuration
  logging: {
    level: 'info',
    enableConsole: false,
    enableFile: true,
    enableRemote: true,
    maxFileSize: '10MB',
    maxFiles: 5,
    format: 'json'
  },

  // Feature Flags
  features: {
    enablePWA: true,
    enableOfflineSupport: true,
    enablePushNotifications: false,
    enableBackgroundSync: false,
    enableGeolocation: true,
    enableCamera: false,
    enableMicrophone: false
  },

  // Environment Variables
  env: {
    NODE_ENV: 'production',
    VITE_APP_ENV: 'production',
    VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
    VITE_APP_BUILD_TIME: new Date().toISOString(),
    VITE_APP_DEBUG: false
  }
};

export default PRODUCTION_CONFIG;